import type { SlateBettingModel, SlateGame } from "./api";

/** Betting fields resolved for one model on one game row. Every field is
 *  `null` when the model does not expose it (`meta.field_* == null`) or the
 *  underlying cell is missing / null. Pure lookup — see spec 007. */
export type ResolvedBettingFields = {
  ev: number | null;
  kellyCapped: number | null;
  bet: boolean | null;
  edge: "no_bet" | "lean" | "strong" | null;
  modelSpreadHome: number | null;
  atsGap: number | null;
  muTotal: number | null;
  ouEdge: number | null;
};

const EDGE_VALUES = new Set(["no_bet", "lean", "strong"]);

function numAt(
  row: Record<string, unknown>,
  key: string | null | undefined,
): number | null {
  if (!key) return null;
  const v = row[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function boolAt(
  row: Record<string, unknown>,
  key: string | null | undefined,
): boolean | null {
  if (!key) return null;
  const v = row[key];
  return typeof v === "boolean" ? v : null;
}

function edgeAt(
  row: Record<string, unknown>,
  key: string | null | undefined,
): "no_bet" | "lean" | "strong" | null {
  if (!key) return null;
  const v = row[key];
  return typeof v === "string" && EDGE_VALUES.has(v)
    ? (v as "no_bet" | "lean" | "strong")
    : null;
}

/** Resolve the betting cells for `game` under `meta`. Reads exclusively via
 *  `meta.field_*` — never a hardcoded `ev`/`edge`/etc. key — so switching the
 *  active model is a pure re-resolution against the same row. */
export function resolveBettingFields(
  game: SlateGame,
  meta: SlateBettingModel,
): ResolvedBettingFields {
  const row = game as unknown as Record<string, unknown>;
  return {
    ev: numAt(row, meta.field_ev),
    kellyCapped: numAt(row, meta.field_kelly_capped),
    bet: boolAt(row, meta.field_bet),
    edge: edgeAt(row, meta.field_edge),
    modelSpreadHome: numAt(row, meta.field_model_spread_home),
    atsGap: numAt(row, meta.field_ats_gap),
    muTotal: numAt(row, meta.field_mu_total),
    ouEdge: numAt(row, meta.field_ou_edge),
  };
}

/** The implicit baseline model used when the API omits `betting_models[]`
 *  (older backend) — uses the legacy un-namespaced column names (FR-003). */
export function syntheticBaselineMeta(): SlateBettingModel {
  return {
    id: "baseline",
    label: "Baseline",
    source: "baseline_precomputed",
    has_spread: true,
    has_total: true,
    field_ev: "ev",
    field_kelly_capped: "kelly_capped",
    field_bet: "bet",
    field_edge: "edge",
    field_mu_total: "mu_total",
    field_ou_edge: "ou_edge",
    field_model_spread_home: "model_spread_home",
    field_ats_gap: "ats_gap",
  };
}

/** Resolve the betting models list to render, tolerating an old API that
 *  omits `betting_models[]` by synthesising a baseline-only registry. */
export function resolveBettingModels(
  models: SlateBettingModel[] | undefined,
): SlateBettingModel[] {
  return models && models.length > 0 ? models : [syntheticBaselineMeta()];
}

/** Pick the active model meta from a stored preference id, falling back to
 *  the first model ("baseline") when unset or when the stored id is not in
 *  the current response. Emits exactly one console.warn on a stale id. */
export function resolveActiveBettingModel(
  storedId: string | undefined,
  models: SlateBettingModel[],
): SlateBettingModel {
  const list = resolveBettingModels(models);
  const fallback = list[0];
  if (storedId == null) return fallback;
  const found = list.find((m) => m.id === storedId);
  if (found) return found;
  console.warn(
    `betting_model_id "${storedId}" not in betting_models[]; falling back to "${fallback.id}"`,
  );
  return fallback;
}
